import { observer } from "mobx-react-lite";
import { FunctionComponent, useContext } from "react";

import {
  dataAttribute,
  DataAttributes,
  FontIcon,
  IContextualMenuListProps,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { CalendarEventAttendeeDto } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { openNewInvoiceFromCalendar } from "@modules/booking/utils/booking.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { getSettledIcon } from "../BookingCalendarEvent.utils.ts";
import { BookingCalendarEventContext } from "../BookingCalendarEventContext.tsx";
import { ContextMenuItemsEnum } from "./ContextMenuItemsEnum.ts";

interface InvoiceSubMenuProps {
  listProps: IContextualMenuListProps | undefined;
  defaultRender?: (props: IContextualMenuListProps) => JSX.Element | null;
  attendee: CalendarEventAttendeeDto;
}

export const AttendeeInvoiceSubMenu: FunctionComponent<InvoiceSubMenuProps> =
  observer(({ listProps, defaultRender, attendee }) => {
    const theme = useTheme();
    const root = useStores();
    const { routing } = root;

    const { calendarEvent } = useContext(BookingCalendarEventContext);

    if (!defaultRender || !listProps) return null;

    const subMenuItemsStyles = {
      color: theme?.palette.themePrimary,
      margin: "auto 4px"
    };

    const getIconProps = () => {
      const styleProps = attendee.invoiceIds?.length
        ? {
            iconName: getSettledIcon(attendee.settlementStatus),
            styles: {
              root: {
                ...subMenuItemsStyles,
                color: theme?.palette.neutralPrimary
              }
            }
          }
        : {
            iconName: "M365InvoicingLogo",
            styles: {
              root: {
                ...subMenuItemsStyles
              }
            }
          };
      return styleProps;
    };

    const onInvoiceClick = () => {
      const invoiceId = attendee.invoiceIds?.[0];
      if (invoiceId) {
        routing.push(
          routes.accounts.invoices.invoice.path({
            id: invoiceId
          })
        );
      } else {
        openNewInvoiceFromCalendar(root, calendarEvent.id, attendee.attendeeId);
      }
    };

    return defaultRender({
      ...listProps,
      items: [
        {
          ...dataAttribute(
            DataAttributes.Element,
            `booking-event-context-invoice-btn-${attendee.attendeeId}`
          ),
          key: "invoice",
          text: ContextMenuItemsEnum.Invoice,
          onRenderContent: () => {
            const iconProps = getIconProps();
            return (
              <Stack
                horizontal
                tokens={{ childrenGap: 4 }}
                styles={{ root: { width: "100%" } }}
                onClick={() => onInvoiceClick()}
              >
                <FontIcon
                  styles={iconProps.styles}
                  iconName={iconProps.iconName}
                />
                <Stack.Item>{ContextMenuItemsEnum.Invoice}</Stack.Item>
              </Stack>
            );
          }
        }
      ]
    });
  });
